/**
 * The terminal. Owns the screen (a list of rendered blocks), the input line
 * with a custom block cursor, command history, tab-completion, and the CRT
 * chrome. Commands themselves live in commands.tsx; this component is the
 * shell loop + presentation host.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { content } from "../content";
import { contentFor } from "../content.i18n";
import { LAUREL_BANNER } from "./ascii";
import {
  completionNames,
  resolve,
  suggest,
  type CommandContext,
  type ThemeName,
} from "./commands";
import { dirAt } from "./filesystem";
import { translate, type OsLang } from "../os/i18n-core";
import { Screensaver } from "./Screensaver";
import { keyClick } from "./sound";
import s from "./Terminal.module.css";

/** Idle time before the screensaver kicks in (fullscreen only). */
const SCREENSAVER_MS = 3 * 60 * 1000;

interface Block {
  id: number;
  node: ReactNode;
}

interface TerminalProps {
  /**
   * Called after the CRT power-off animation when the user runs `exit`.
   * Full-screen: the App swaps in SliceOS. Windowed (inside the OS):
   * the host app closes its window.
   */
  onExit?: () => void;
  /** "windowed" makes the shell fill its container instead of the viewport. */
  variant?: "fullscreen" | "windowed";
  /** OS display language (the résumé content itself stays English). */
  lang?: OsLang;
}

const PILLS = ["help", "about", "experience", "projects", "skills", "cv", "contact"];

/** The banner block, rendered by the host (it owns the <pre> chrome). */
function Banner({ lang = "en" }: { lang?: OsLang }) {
  return (
    <div className={s.bannerWrap}>
      <pre className={s.banner} aria-label="LAUREL">
        {LAUREL_BANNER}
      </pre>
      <div className={s.tagline}>{contentFor(lang).tagline}</div>
    </div>
  );
}

/** First-run welcome shown under the banner. */
function Welcome({ lang = "en" }: { lang?: OsLang }) {
  if (lang !== "en") {
    return (
      <div className={s.welcome}>
        <div>
          {translate(
            lang,
            "Welcome. This is an interactive résumé — a little shell standing in for a portfolio.",
          )}
        </div>
        <div>
          {translate(lang, "Type help to see what's available, or cv to download my resume.")}
        </div>
      </div>
    );
  }
  return (
    <div className={s.welcome}>
      <div>
        Welcome. This is an interactive résumé — a little shell standing in for
        a portfolio.
      </div>
      <div>
        Type <span className={s.accent}>help</span> to see what's available, or{" "}
        <span className={s.accent}>cv</span> to download my resume.
      </div>
    </div>
  );
}

export function Terminal({ onExit, variant = "fullscreen" , lang = "en" }: TerminalProps) {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [poweringOff, setPoweringOff] = useState(false);
  const [input, setInput] = useState("");
  const [cursor, setCursor] = useState(0);
  const [cwd, setCwd] = useState<string[]>([]);
  const [theme, setTheme] = useState<ThemeName>("green");
  const [history, setHistory] = useState<string[]>([]);
  const [histIndex, setHistIndex] = useState<number | null>(null);
  const [booting, setBooting] = useState(true);
  const [clock, setClock] = useState("");
  const [saverActive, setSaverActive] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const blockId = useRef(0);
  const didInit = useRef(false);
  const typing = useRef(false);
  const skipBoot = useRef(false);
  const reactId = useId();

  const push = useCallback((node: ReactNode) => {
    setBlocks((prev) => [...prev, { id: blockId.current++, node }]);
  }, []);

  const pushId = useCallback((node: ReactNode) => {
    const id = blockId.current++;
    setBlocks((prev) => [...prev, { id, node }]);
    return id;
  }, []);

  const setNode = useCallback((id: number, node: ReactNode) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { id, node } : b)));
  }, []);

  // Apply the theme to the document so CSS variable sets switch.
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  // Windowed (inside SliceOS) the shell must not yank global focus — only
  // grab it when we're the whole page, or when focus is already inside us
  // (e.g. after the user clicked our window).
  const politeFocus = useCallback(() => {
    if (
      variant === "fullscreen" ||
      rootRef.current?.contains(document.activeElement)
    ) {
      focusInput();
    }
  }, [variant, focusInput]);

  // Click anywhere on the screen to focus the prompt — except on links and
  // buttons (let those do their thing) or when the user has highlighted text
  // (a plain click collapses the selection; a real highlight does not).
  const handleScreenClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("a, button, input, textarea")) return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return;
      focusInput();
    },
    [focusInput],
  );

  // Animated boot: prints a short boot log, the banner, then types the
  // welcome out. Skippable (any key / click) and skipped entirely under
  // prefers-reduced-motion. Guarded so StrictMode can't run it twice.
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
    // Must match the visible text of <Welcome> exactly (no quotes around
    // help/cv) so nothing reflows when the typed text settles into it.
    const welcomeText =
      lang !== "en"
        ? translate(
            lang,
            "Welcome. This is an interactive résumé — a little shell standing in for a portfolio.",
          ) +
          "\n" +
          translate(lang, "Type help to see what's available, or cv to download my resume.")
        : "Welcome. This is an interactive résumé — a little shell standing in for a portfolio.\n" +
          "Type help to see what's available, or cv to download my resume.";

    const finish = () => {
      setBlocks([
        { id: blockId.current++, node: <Banner lang={lang} /> },
        { id: blockId.current++, node: <Welcome lang={lang} /> },
      ]);
      setBooting(false);
      requestAnimationFrame(politeFocus);
    };

    const reduce =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

    void (async () => {
      if (reduce) return finish();

      const bootLines = [
        "laurel-sh v1.0.0 (tty1)",
        translate(lang, "loading modules … about experience projects skills … ok"),
        translate(lang, "mounting /home/laurel … ok"),
        translate(lang, "starting shell … ok"),
        "",
      ];
      for (const line of bootLines) {
        if (skipBoot.current) return finish();
        push(<div className={s.bootLine}>{line || " "}</div>);
        await sleep(line ? 200 : 120);
      }
      if (skipBoot.current) return finish();

      push(<Banner lang={lang} />);
      await sleep(180);

      const id = pushId(<div className={s.welcome} />);
      let shown = "";
      for (const ch of welcomeText) {
        if (skipBoot.current) break;
        shown += ch;
        // Mirror <Welcome>'s structure exactly (one <div> per line) so spacing
        // is identical and nothing shifts when it settles.
        const lines = shown.split("\n");
        setNode(
          id,
          <div className={s.welcome}>
            {lines.map((ln, i) => (
              <div key={i}>
                {ln}
                {i === lines.length - 1 && (
                  <span className={s.cursor}>&nbsp;</span>
                )}
              </div>
            ))}
          </div>,
        );
        await sleep(ch === "\n" ? 140 : 15);
      }
      setNode(id, <Welcome lang={lang} />);
      setBooting(false);
      requestAnimationFrame(politeFocus);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Let any key / click skip the boot animation. Fullscreen the whole page
  // is ours, so listen on window; windowed, listen only on our own subtree
  // so keystrokes in other SliceOS apps don't get intercepted.
  useEffect(() => {
    if (!booting) return;
    const skip = () => {
      skipBoot.current = true;
    };
    const target: Window | HTMLElement | null =
      variant === "fullscreen" ? window : rootRef.current;
    if (!target) return;
    target.addEventListener("keydown", skip);
    target.addEventListener("mousedown", skip);
    return () => {
      target.removeEventListener("keydown", skip);
      target.removeEventListener("mousedown", skip);
    };
  }, [booting, variant]);

  // Live clock in the status bar. (Formatter cached — toLocaleTimeString
  // constructs a fresh Intl.DateTimeFormat per call.)
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const tick = () => setClock(fmt.format(new Date()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // Keep the newest output in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [blocks]);

  // Idle screensaver (fullscreen only): matrix rain after a few quiet
  // minutes; any activity wakes the shell back up.
  useEffect(() => {
    if (variant !== "fullscreen") return;
    let timer = 0;
    const arm = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setSaverActive(true), SCREENSAVER_MS);
    };
    const wake = () => {
      setSaverActive(false);
      arm();
    };
    arm();
    window.addEventListener("keydown", wake);
    window.addEventListener("mousemove", wake, { passive: true });
    window.addEventListener("mousedown", wake);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("keydown", wake);
      window.removeEventListener("mousemove", wake);
      window.removeEventListener("mousedown", wake);
    };
  }, [variant]);

  // `exit` prints its farewell, the screen stays readable for a beat, then
  // the host takes over. Windowed (inside SliceOS) the CRT collapses as the
  // window closes; fullscreen, the host (App) shrinks the whole terminal
  // away to reveal the desktop, so we skip the collapse and just hand off.
  const powerOff = useCallback(() => {
    if (!onExit || typing.current) return;
    typing.current = true; // swallow further keystrokes
    if (variant === "windowed") {
      window.setTimeout(() => setPoweringOff(true), 500);
      window.setTimeout(onExit, 1250);
    } else {
      window.setTimeout(onExit, 650);
    }
  }, [onExit, variant]);

  const ctx: CommandContext = useMemo(
    () => ({
      lang,
      cwd,
      setCwd,
      theme,
      setTheme,
      clear: () => setBlocks([]),
      history,
      exit: onExit ? powerOff : null,
      variant,
    }),
    [cwd, theme, history, onExit, powerOff, variant, lang],
  );

  const prompt = useMemo(() => {
    const dir = cwd.length === 0 ? "~" : "~/" + cwd.join("/");
    return `laurel@portfolio:${dir}$`;
  }, [cwd]);

  const dispatch = useCallback(
    (raw: string) => {
      const line = raw.trim();
      // Echo the entered line with its prompt.
      const echoed = (
        <div className={s.echo}>
          <span className={s.promptUser}>laurel@portfolio</span>
          <span className={s.dim}>:</span>
          <span className={s.promptDir}>
            {cwd.length === 0 ? "~" : "~/" + cwd.join("/")}
          </span>
          <span className={s.dim}>$ </span>
          <span>{line}</span>
        </div>
      );

      if (line === "") {
        push(echoed);
        return;
      }

      push(echoed);
      setHistory((prev) => [...prev, line]);

      const [name, ...args] = line.split(/\s+/);
      const cmd = resolve(name);

      if (!cmd) {
        const guess = suggest(name);
        push(
          <div className={s.block}>
            <span className={s.err}>command not found: {name}</span>
            <span className={s.dim}>
              {" "}
              {guess ? (
                <>
                  — did you mean <code className={s.kbd}>{guess}</code>?
                </>
              ) : (
                <>
                  — type <code className={s.kbd}>help</code> for a list.
                </>
              )}
            </span>
          </div>,
        );
        return;
      }

      const out = cmd.run(args, ctx);
      if (out != null) push(out);
    },
    [ctx, cwd, push],
  );

  // Clicking a pill "types" the command out char-by-char, then submits it —
  // so it feels like the shell is being driven, not just jumped to output.
  const typeAndRun = useCallback(
    (cmd: string) => {
      if (typing.current) return;
      typing.current = true;
      focusInput();
      setHistIndex(null);
      setInput("");
      setCursor(0);

      let i = 0;
      const step = () => {
        i += 1;
        const slice = cmd.slice(0, i);
        setInput(slice);
        setCursor(slice.length);
        if (i < cmd.length) {
          setTimeout(step, 42);
        } else {
          setTimeout(() => {
            dispatch(cmd);
            setInput("");
            setCursor(0);
            typing.current = false;
            focusInput();
          }, 180);
        }
      };
      setTimeout(step, 70);
    },
    [dispatch, focusInput],
  );

  const syncCursor = useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) setCursor(el.selectionStart ?? el.value.length);
    });
  }, []);

  const tabComplete = useCallback(() => {
    const tokens = input.split(/\s+/);
    const isFirst = tokens.length === 1;
    const fragment = tokens[tokens.length - 1] ?? "";

    let pool: string[];
    if (isFirst) {
      pool = completionNames();
    } else {
      // Complete against entries in the current directory.
      const dir = dirAt(cwd);
      pool = dir ? Object.keys(dir.children) : [];
    }

    const matches = pool.filter((n) => n.startsWith(fragment));
    if (matches.length === 0) return;

    let completion = matches[0];
    if (matches.length > 1) {
      // Longest common prefix; list options if it doesn't grow.
      completion = matches.reduce((acc, m) => {
        let i = 0;
        while (i < acc.length && i < m.length && acc[i] === m[i]) i++;
        return acc.slice(0, i);
      });
      if (completion === fragment) {
        push(
          <div className={s.block}>
            <div className={s.lsGrid}>
              {matches.map((m) => (
                <span key={m} className={s.lsFile}>
                  {m}
                </span>
              ))}
            </div>
          </div>,
        );
        return;
      }
    }

    const next = [...tokens.slice(0, -1), completion].join(" ");
    setInput(next);
    setCursor(next.length);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) el.setSelectionRange(next.length, next.length);
    });
  }, [input, cwd, push]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Ignore keystrokes while a pill is auto-typing.
      if (typing.current) {
        e.preventDefault();
        return;
      }

      // Mechanical-keyboard tick for real typing (not bare modifiers).
      if (e.key.length === 1 || e.key === "Enter" || e.key === "Backspace") {
        keyClick(e.key === "Enter");
      }

      if (e.key === "Enter") {
        dispatch(input);
        setInput("");
        setCursor(0);
        setHistIndex(null);
        return;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        tabComplete();
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (history.length === 0) return;
        const idx = histIndex === null ? history.length - 1 : Math.max(0, histIndex - 1);
        setHistIndex(idx);
        const val = history[idx];
        setInput(val);
        setCursor(val.length);
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (histIndex === null) return;
        const idx = histIndex + 1;
        if (idx >= history.length) {
          setHistIndex(null);
          setInput("");
          setCursor(0);
        } else {
          setHistIndex(idx);
          setInput(history[idx]);
          setCursor(history[idx].length);
        }
        return;
      }

      if (e.key === "l" && e.ctrlKey) {
        e.preventDefault();
        setBlocks([]);
        return;
      }

      syncCursor();
    },
    [input, history, histIndex, dispatch, tabComplete, syncCursor],
  );

  // Render the input line as text + a block cursor sitting on selectionStart.
  const before = input.slice(0, cursor);
  const at = input.slice(cursor, cursor + 1) || " ";
  const after = input.slice(cursor + 1);

  const crtClass = [
    s.crt,
    variant === "windowed" ? s.crtWindowed : "",
    poweringOff ? s.crtOff : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={rootRef} className={crtClass} onClick={handleScreenClick}>
      <div className={s.scanlines} aria-hidden />
      <div className={s.flicker} aria-hidden />
      <div className={s.vignette} aria-hidden />
      {saverActive && <Screensaver />}

      {/* Sticky title bar. Theme is changed via the `theme` command. */}
      <div className={s.statusbar}>
        <span className={s.dim}>
          laurel-sh — {content.name.toLowerCase().replace(/\s+/g, "-")}
        </span>
        <span className={s.clock} aria-hidden>
          {clock}
        </span>
      </div>

      <div className={s.screen} ref={scrollRef}>
        {blocks.map((b) => (
          <div key={b.id}>{b.node}</div>
        ))}

        {/* Active input line — hidden while the boot animation plays. */}
        {!booting && (
          <div className={s.inputLine}>
            <span className={s.promptUser}>laurel@portfolio</span>
            <span className={s.dim}>:</span>
            <span className={s.promptDir}>
              {cwd.length === 0 ? "~" : "~/" + cwd.join("/")}
            </span>
            <span className={s.dim}>$&nbsp;</span>
            <span className={s.typed}>
              {before}
              <span className={s.cursor}>{at}</span>
              {after}
            </span>
            <input
              ref={inputRef}
              className={s.hiddenInput}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setCursor(e.target.selectionStart ?? e.target.value.length);
              }}
              onKeyDown={onKeyDown}
              onKeyUp={syncCursor}
              onClick={syncCursor}
              autoFocus={variant === "fullscreen"}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              aria-label="terminal input"
              aria-describedby={reactId}
            />
          </div>
        )}
        <span id={reactId} hidden>
          {prompt} interactive terminal. Type help and press enter.
        </span>
      </div>

      {/* Quick-command pills — pinned to the bottom. Essential on touch,
          handy everywhere. Clicking auto-types + runs the command. */}
      <div className={s.pillbar} aria-label="quick commands">
        {PILLS.map((p) => (
          <button key={p} className={s.pill} onClick={() => typeAndRun(p)}>
            {p}
          </button>
        ))}
      </div>

      {/* A sticky note "taped to the glass" — the hint that the résumé
          shell is running on something bigger. Just paper: not clickable.
          Fullscreen only; the windowed shell already lives inside the
          thing the note hints at. */}
      {variant === "fullscreen" && !booting && (
        <div role="note" className={s.stickyNote}>
          <span className={s.stickyTape} aria-hidden />
          <span className={s.stickyLine}>
            This shell isn't the
            <br />
            whole machine.
          </span>
          <span className={s.stickyLine}>
            type <span className={s.stickyCmd}>exit</span> ⏎
          </span>
          <span className={s.stickySig}>— L.</span>
        </div>
      )}
    </div>
  );
}
